import { OrderStatus, OrderType, type OrderStatusValue, type OrderTypeValue } from 'sama-shared';

export interface OrderStatusMeta {
  title: string;
  subtitle: string;
  badgeText: string;
  badgeColor: string;
  iconName: 'wallet' | 'fire' | 'check-circle' | 'car' | 'gift' | 'close-circle';
  isActive: boolean;
}

/**
 * Determina si una orden está en curso (activa)
 * Estados 1..7 (created, pending_payment, paid, in_process, ready, on_the_way, at_delivery_point)
 */
export const isOrderActive = (status?: number | null): boolean => {
  if (status === undefined || status === null) return false;
  return (
    status >= OrderStatus.created &&
    status < OrderStatus.delivered &&
    status !== OrderStatus.cancelled &&
    status !== OrderStatus.failled_paid
  );
};

/**
 * Retorna metadatos visuales y textos claros en español para cada estado de la orden.
 */
export const getOrderStatusMeta = (
  status: OrderStatusValue,
  type: OrderTypeValue = OrderType.delivery
): OrderStatusMeta => {
  const isDelivery = type === OrderType.delivery;

  switch (status) {
    case OrderStatus.created:
    case OrderStatus.pending_payment:
      return {
        title: 'Verificación de pago',
        subtitle: 'Estamos validando tu pago con el comercio.',
        badgeText: 'Por verificar',
        badgeColor: '#D97706', // amber
        iconName: 'wallet',
        isActive: true,
      };

    case OrderStatus.paid:
      return {
        title: '¡Pago confirmado!',
        subtitle: 'El comercio ha confirmado tu pago. Pasando a cocina...',
        badgeText: 'Pago Aprobado',
        badgeColor: '#16A34A', // green
        iconName: 'check-circle',
        isActive: true,
      };

    case OrderStatus.in_process:
      return {
        title: '👨‍🍳 En cocina / Preparación',
        subtitle: 'Tu orden está siendo elaborada por el equipo del restaurante.',
        badgeText: 'En Cocina',
        badgeColor: '#EA580C', // orange
        iconName: 'fire',
        isActive: true,
      };

    case OrderStatus.ready_for_delivery:
      return {
        title: isDelivery ? '🥡 ¡Pedido listo y empacado!' : '🥡 ¡Listo para retirar!',
        subtitle: isDelivery
          ? 'Tu comida está empacada y esperando al repartidor.'
          : 'Ya puedes acercarte a la sucursal a retirar tu pedido.',
        badgeText: isDelivery ? 'Listo para entrega' : 'Listo para retirar',
        badgeColor: '#2563EB', // blue
        iconName: 'check-circle',
        isActive: true,
      };

    case OrderStatus.on_the_way:
    case OrderStatus.at_delivery_point:
      return {
        title: '🛵 Repartidor en camino',
        subtitle:
          status === OrderStatus.at_delivery_point
            ? '¡El repartidor ya está en tu punto de entrega!'
            : 'El repartidor va en camino con tu comida.',
        badgeText: 'En Camino',
        badgeColor: '#7C3AED', // purple
        iconName: 'car',
        isActive: true,
      };

    case OrderStatus.delivered:
      return {
        title: '🎉 ¡Pedido entregado!',
        subtitle: 'Esperamos que disfrutes tu comida. ¡Gracias por tu compra!',
        badgeText: 'Entregado',
        badgeColor: '#059669', // emerald
        iconName: 'gift',
        isActive: false,
      };

    case OrderStatus.cancelled:
    case OrderStatus.failled_paid:
    default:
      return {
        title: status === OrderStatus.failled_paid ? 'Pago fallido' : 'Pedido cancelado',
        subtitle: 'Si tienes alguna duda o reclamo, comunícate directamente con el local.',
        badgeText: status === OrderStatus.failled_paid ? 'Pago Fallido' : 'Cancelado',
        badgeColor: '#DC2626', // red
        iconName: 'close-circle',
        isActive: false,
      };
  }
};

export const getOrderStepsConfig = (type: OrderTypeValue, currentStatus: OrderStatusValue) => {
  const isCancelled =
    currentStatus === OrderStatus.cancelled || currentStatus === OrderStatus.failled_paid;

  if (type === OrderType.delivery) {
    // Flujo para DELIVERY (5 Pasos)
    const steps = [
      { title: 'Pedido Recibido', description: 'Confirmado y en espera' },
      { title: 'En Preparación', description: 'Tu pedido se está elaborando en cocina' },
      { title: 'Listo para Enviar', description: 'Empacado y listo' },
      { title: 'En Camino', description: 'El repartidor lleva tu pedido' },
      { title: 'Entregado', description: '¡Que lo disfrutes!' },
    ];

    let currentStep = 0;
    if (currentStatus < OrderStatus.in_process) {
      currentStep = 0;
    } else if (currentStatus === OrderStatus.in_process) {
      currentStep = 1;
    } else if (currentStatus === OrderStatus.ready_for_delivery) {
      currentStep = 2;
    } else if (
      currentStatus === OrderStatus.on_the_way ||
      currentStatus === OrderStatus.at_delivery_point
    ) {
      currentStep = 3;
    } else if (currentStatus >= OrderStatus.delivered) {
      currentStep = 4;
    }

    return { steps, currentStep, isCancelled };
  } else {
    // Flujo para PICKUP (4 Pasos)
    const steps = [
      { title: 'Pedido Recibido', description: 'Confirmado y en espera' },
      { title: 'En Preparación', description: 'Tu pedido se está elaborando en cocina' },
      { title: 'Listo para Retirar', description: 'Puedes pasar por el local' },
      { title: 'Completado', description: 'Pedido retirado con éxito' },
    ];

    let currentStep = 0;
    if (currentStatus < OrderStatus.in_process) {
      currentStep = 0;
    } else if (currentStatus === OrderStatus.in_process) {
      currentStep = 1;
    } else if (currentStatus === OrderStatus.ready_for_delivery) {
      currentStep = 2;
    } else if (currentStatus >= OrderStatus.delivered) {
      currentStep = 3;
    }

    return { steps, currentStep, isCancelled };
  }
};
